import { color, severityBand } from '../tokens';
import { useSynth, useSynthNav } from '../SynthesisContext';
import { asText, cleanLabel } from '../asText';
import { countOf } from '../../../utils/pluralize';

function validWho(v: string): boolean {
  const s = asText(v).trim();
  if (!s) return false;
  if (/^(unknown|\*note|survey count|n\/a|na|null|undefined|-|—)$/i.test(s)) return false;
  if (/^\d+\s+responses$/i.test(s)) return false;
  if (/^[A-Z0-9_/*+\- ]{3,}$/.test(s) && s === s.toUpperCase()) return false;
  return true;
}

export default function ByStakeholder() {
  const { themes, quotes } = useSynth();
  const { setActive, setExpandedThemeRank } = useSynthNav();

  if (!themes.length) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
            background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
          }}>BY STAKEHOLDER</span>
        </div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
          Themes by stakeholder
        </h2>
        <p style={{ marginTop: 8, fontSize: 13, color: color.textMuted }}>No themes are available yet for stakeholder inversion.</p>
      </div>
    );
  }

  const themeByRank = new Map(themes.map((t) => [t.rank, t]));
  const byWho = new Map<string, { role: string; themeRanks: Set<number> }>();
  for (const q of quotes) {
    const who = asText(q.who).trim();
    if (!validWho(who)) continue;
    if (!byWho.has(who)) byWho.set(who, { role: asText(q.role), themeRanks: new Set() });
    const item = byWho.get(who)!;
    if (!item.role && asText(q.role).trim()) item.role = asText(q.role).trim();
    if (q.themeIdx && themeByRank.has(q.themeIdx)) item.themeRanks.add(q.themeIdx);
  }

  if (!quotes.length || byWho.size === 0) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
            background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
          }}>BY STAKEHOLDER</span>
        </div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
          Themes by stakeholder
        </h2>
        <p style={{ marginTop: 8, fontSize: 13, color: color.textMuted }}>
          No quotes with usable stakeholder attribution were found.
        </p>
      </div>
    );
  }

  const rows = Array.from(byWho.entries())
    .map(([name, data]) => {
      const linked = Array.from(data.themeRanks)
        .map((rank) => themeByRank.get(rank))
        .filter((x): x is NonNullable<typeof x> => !!x)
        .sort((a, b) => (b.sevScore || 0) - (a.sevScore || 0))
        .slice(0, 7);
      return {
        name,
        role: data.role,
        themeCount: data.themeRanks.size,
        themes: linked,
      };
    })
    .sort((a, b) => b.themeCount - a.themeCount || a.name.localeCompare(b.name));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>BY STAKEHOLDER</span>
      </div>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
        Themes by stakeholder
      </h2>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
        Showing {countOf(rows.length, 'stakeholder')} derived from quote attribution.
      </p>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row) => (
          <div key={row.name} style={{ background: color.surface, border: `1px solid ${color.hairline}`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: color.ink }}>{row.name}</span>
              {row.role && <span style={{ fontSize: 12, color: color.textMuted }}>{row.role}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: color.textMuted }}>{countOf(row.themeCount, 'theme')}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {row.themes.length > 0 ? row.themes.map((t) => {
                const sev = severityBand[t.sev] || severityBand.MED;
                return (
                  <button
                    key={`${row.name}-${t.rank}`}
                    onClick={() => { setExpandedThemeRank(t.rank); setActive('key-themes'); }}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 999,
                      border: `1px solid ${sev.border}`,
                      background: sev.bg,
                      color: sev.fg,
                      padding: '3px 8px',
                      fontSize: 11.5,
                    }}
                    title={`Open theme #${t.rank}: ${cleanLabel(t.name)}`}
                  >
                    #{t.rank} {cleanLabel(t.name)}
                  </button>
                );
              }) : (
                <span style={{ fontSize: 12, color: color.textFaint }}>No themes linked via quotes.</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
